import {
  connectEyeDevice,
  startStreaming,
  disconnectEyeDevice,
} from "../services/eyeBluetooth.service";
import { useState, useEffect, useRef, useCallback } from "react";

// ── Types ───────────────────────────────────────────────────────────────
type HzData = number | { ch1: number; ch2: number; ch3: number };

type ToastType = "success" | "error" | "info" | "warning" | "bluetooth";

interface ToastOptions {
  type: ToastType;
  title: string;
  message: string;
  duration?: number;
}

interface Props {
  onHzChange: (data: HzData) => void;
  onConnectionChange: (status: boolean) => void;
  toast: (options: ToastOptions) => void;
}

// Extend Navigator to include bluetooth
interface BluetoothNavigator extends Navigator {
  bluetooth?: any;
}

export default function BluetoothSensor({
  onHzChange,
  onConnectionChange,
  toast,
}: Props) {
  const [connected, setConnected] = useState<boolean>(false);
  const [connecting, setConnecting] = useState<boolean>(false);
  const [deviceName, setDeviceName] = useState<string | null>(null);

  const deviceRef = useRef<any | null>(null);
  const connectedRef = useRef<boolean>(false);

  const navigatorWithBluetooth = navigator as BluetoothNavigator;

  // Keep ref in sync
  useEffect(() => {
    connectedRef.current = connected;
  }, [connected]);

  // ── Unexpected disconnect handler ─────────────────────────────────────
  const handleUnexpectedDisconnect = useCallback((): void => {
    if (!connectedRef.current) return;

    connectedRef.current = false;
    setConnected(false);
    setDeviceName(null);

    onConnectionChange(false);
    onHzChange(0);

    toast({
      type: "warning",
      title: "Device Disconnected",
      message: "Bluetooth device lost connection unexpectedly.",
      duration: 5000,
    });
  }, [onConnectionChange, onHzChange, toast]);

  // Register / deregister listener
  useEffect(() => {
    const device = deviceRef.current;
    if (!device) return;

    device.addEventListener(
      "gattserverdisconnected",
      handleUnexpectedDisconnect
    );

    return () => {
      device.removeEventListener(
        "gattserverdisconnected",
        handleUnexpectedDisconnect
      );
    };
  }, [handleUnexpectedDisconnect, connected]);

  // ── Connect ───────────────────────────────────────────────────────────
  const connect = async (): Promise<void> => {
    if (!navigatorWithBluetooth.bluetooth) {
      toast({
        type: "error",
        title: "Bluetooth Not Supported",
        message: "Please use Chrome on desktop (Windows/Mac/Linux).",
        duration: 6000,
      });
      return;
    }

    setConnecting(true);

    try {
      const device: any = await connectEyeDevice();
      deviceRef.current = device;

      await startStreaming((data: HzData) => {
        onHzChange(data);
      });

      setConnected(true);
      connectedRef.current = true;

      setDeviceName(device.name || "Unknown Device");
      onConnectionChange(true);

      toast({
        type: "success",
        title: "Dream Sensor Connected",
        message: `Connected to: ${device.name || "Unknown Device"}`,
        duration: 3000,
      });
    } catch (err: any) {
      deviceRef.current = null;
      setConnected(false);
      connectedRef.current = false;

      if (
        err?.name === "NotFoundError" ||
        err?.message?.includes("cancelled") ||
        err?.message?.includes("chooser")
      ) {
        toast({
          type: "info",
          title: "Connection Cancelled",
          message: "No device was selected.",
          duration: 3000,
        });
        return;
      }

      if (
        err?.name === "NotSupportedError" ||
        err?.message?.toLowerCase().includes("bluetooth")
      ) {
        toast({
          type: "error",
          title: "Bluetooth Unavailable",
          message: "Please enable Bluetooth and try again.",
          duration: 5000,
        });
        return;
      }

      if (err?.name === "SecurityError") {
        toast({
          type: "error",
          title: "Permission Denied",
          message: "Bluetooth access was blocked.",
          duration: 5000,
        });
        return;
      }

      toast({
        type: "error",
        title: "Connection Failed",
        message: err?.message ?? "Unknown error.",
        duration: 5000,
      });
    } finally {
      setConnecting(false);
    }
  };

  // ── Disconnect ────────────────────────────────────────────────────────
  const disconnect = async (): Promise<void> => {
    connectedRef.current = false;
    setConnected(false);
    setDeviceName(null);

    try {
      await disconnectEyeDevice();
    } catch (_) {}

    deviceRef.current = null;

    onConnectionChange(false);
    onHzChange(0);

    toast({
      type: "bluetooth",
      title: "Disconnected",
      message: "Dream sensor disconnected.",
      duration: 2500,
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <button
        className={`btn ${
          connected ? "disconnect" : connecting ? "connecting" : "connect"
        }`}
        onClick={connected ? disconnect : connect}
        disabled={connecting}
      >
        {connecting
          ? "⏳ Connecting..."
          : connected
          ? "🔴 Disconnect Dream Sensor"
          : "🟢 Connect Dream Sensor"}
      </button>

      {connected && deviceName && (
        <div
          style={{
            fontSize: "11px",
            color: "#34d399",
            background: "#0f2a1a",
            border: "1px solid #34d39933",
            borderRadius: "6px",
            padding: "4px 10px",
            textAlign: "center",
          }}
        >
          📡 {deviceName}
        </div>
      )}

      {!navigatorWithBluetooth.bluetooth && (
        <div
          style={{
            fontSize: "11px",
            color: "#f87171",
            background: "#1e0b0b",
            border: "1px solid #f8717133",
            borderRadius: "6px",
            padding: "4px 10px",
            textAlign: "center",
          }}
        >
          ⚠️ Use Chrome desktop for Bluetooth
        </div>
      )}
    </div>
  );
}